import PopupBasicLazy from 'src/game/components/popups/PopupBasicLazy';
import i18n from 'src/lib/i18n/i18n';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import View from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { Feature } from 'src/replicant/analytics';
import uiConfig from 'src/lib/ui/config';
import { AssetGroup } from '@play-co/timestep-core';
import { Avatar } from 'src/game/components/shared/Avatar';
import { SpeciesAvatar } from '../components/SpeciesAvatar';
import type { PetSpeciesID } from 'src/replicant/pet';
import type { AvatarFrameId } from 'src/replicant/profile/frames';
import { FriendConfirmAction } from '../friends.analytics.schema';

type CloseHandler = (result: FriendConfirmAction) => void;

export type PopupFriendRequestConfirmArgs = {
  player: {
    name: string;
    photo: string;
    frame: AvatarFrameId;
    species: PetSpeciesID;
  };
};

export default class PopupFriendRequestConfirm extends PopupBasicLazy {
  private buttonCancel: ButtonScaleViewWithText;
  private buttonOk: ButtonScaleViewWithText;
  private close: CloseHandler;
  private playerPhoto!: Avatar;
  private playerName!: LangBitmapFontTextView;
  private speciesAvatar: SpeciesAvatar;

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      zIndex: 10000,
      width: uiConfig.popups.buruburu.width,
      height: 540,
      darkerBg: false,
      superview: opts.superview,
      close: () => opts.close('dismissed'),
      titleIcon: { image: 'assets/ui/friends/icon_friends_title.png' },
      tracking: {
        feature: Feature.FRIENDS._,
        $subFeature: Feature.FRIENDS.POPUP_CONFIRM_REQUEST._,
      },
    });

    this.close = opts.close;
    this.buttonClose.removeFromSuperview();

    const avatarSize = 140;
    const avatarY = 100;

    // Avatar centered in popup
    this.playerPhoto = new Avatar({
      superview: this.box,
      centerOnOrigin: true,
      width: avatarSize,
      height: avatarSize,
      x: this.box.style.width / 2,
      y: avatarY + avatarSize / 2,
    });

    // Species avatar (bottom-right of player avatar)
    this.speciesAvatar = new SpeciesAvatar({
      superview: this.box,
      centerOnOrigin: true,
      x: this.box.style.width / 2 + avatarSize / 2 - 20,
      y: avatarY + avatarSize - 20,
      scale: 0.4,
    });

    // Player name centered below avatar
    this.playerName = new LangBitmapFontTextView({
      superview: this.box,
      size: 36,
      font: 'Body',
      color: '#9A7171',
      align: 'center',
      verticalAlign: 'center',
      width: this.box.style.width - 40,
      height: 48,
      x: 20,
      y: avatarY + avatarSize + 10,
    });

    // Message "Want to be your friend" centered below player name
    this.message.updateOpts({
      size: 30,
      height: 48,
      width: this.box.style.width - 40,
      x: 20,
      y: this.playerName.style.y + this.playerName.style.height + 5,
      align: 'center',
      zIndex: 1,
    });

    // Divider line below message
    new View({
      superview: this.box,
      width: this.box.style.width - 100,
      x: 50,
      y: this.message.style.y + this.message.style.height + 15,
      height: 4,
      backgroundColor: '#FDC381',
    });

    const buttonWidth = 230;
    const buttonHeight = 100;

    this.buttonCancel = new ButtonScaleViewWithText({
      superview: this.box,
      ...uiConfig.buttons.cancel,
      labelOffsetY: -1,
      fontSize: 46,
      font: 'Body',
      width: buttonWidth,
      height: buttonHeight,
      x: (this.box.style.width - buttonWidth) / 2 - 10,
      y: this.box.style.height - 40 - buttonHeight / 2,
      centerOnOrigin: true,
      localeText: () => i18n('friends.friendRequestConfirm.decline'),
      onClick: async () => {
        sounds.sfx.play('cancel');
        return opts.close('rejected');
      },
      tracking: {
        feature: Feature.FRIENDS._,
        $subFeature: Feature.FRIENDS.POPUP_CONFIRM_REQUEST.CLOSE,
      },
    });

    this.buttonOk = new ButtonScaleViewWithText({
      superview: this.box,
      ...uiConfig.buttons.secondary,
      labelOffsetY: -1,
      fontSize: 46,
      font: 'Body',
      width: buttonWidth,
      height: buttonHeight,
      x: (this.box.style.width + buttonWidth) / 2 + 10,
      y: this.box.style.height - 40 - buttonHeight / 2,
      centerOnOrigin: true,
      localeText: () => i18n('friends.friendRequestConfirm.accept'),
      onClick: async () => {
        sounds.sfx.play('ok');
        return opts.close('accepted');
      },
      tracking: {
        feature: Feature.FRIENDS._,
        $subFeature: Feature.FRIENDS.POPUP_CONFIRM_REQUEST.OK,
      },
    });
  }

  public getAssetsGroup() {
    return new AssetGroup(['assets/ui/friends/icon_friends_title.png']);
  }

  public init(args: PopupFriendRequestConfirmArgs) {
    super.init({
      title: i18n('friends.friendRequestConfirm.title'),
      message: i18n('friends.friendRequestConfirm.message'),
    });
    this.playerPhoto.updateImage(args.player.photo);
    this.playerPhoto.setFrame(args.player.frame ?? 'default');
    this.playerName.localeText = () => args.player.name;
    this.speciesAvatar.setSpecies(args.player.species);
  }
}
